/**
 * When a submitted SDL assessment may be reopened for correction.
 *
 * Submitting is not final: an owner who spots a mistake can put the assessment
 * back into draft, fix it and submit again. The boundary is the assessment
 * period, not the user's role -- once the period closes the score has been
 * reported on and the backend refuses to update, submit or reopen it.
 *
 * Kept in one place because the rule is applied in two: the product page's SDL
 * panel decides whether to offer a route back into the form, and the
 * assessment page decides whether to offer the control itself. Those two
 * disagreeing is how the previous version broke -- the UI offered "Return to
 * Draft" to any admin while the API allowed only global admins, so the button
 * returned 403 for everyone else who could see it.
 */
export function canReopenSubmittedAssessment(params: {
  /** Assessment status, as returned by the API. */
  status: string | null | undefined;
  /** Whether the assessment's period still accepts changes. */
  periodIsOpen: boolean | null | undefined;
  /** Whether this user may manage assessments for the product. */
  canManage: boolean;
}): boolean {
  const { status, periodIsOpen, canManage } = params;

  // Only a submitted assessment needs reopening; a draft is already editable.
  if (status !== "submitted") return false;

  // Treat unknown period state as closed. The period list may not have loaded
  // yet, and offering a control the backend would reject is worse than
  // briefly withholding one it would accept.
  if (!periodIsOpen) return false;

  return canManage;
}
